/**
 * Digital Menu Browser (DMB) - Mobile-optimized menu browsing
 *
 * Public page for customers to browse menu during AI Host calls.
 * URL: /m/:sessionId
 *
 * No authentication required - session_id is the access token.
 * Sessions expire after 30 minutes.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Plus, Minus, ShoppingCart, Phone, ChefHat, Leaf, AlertTriangle, X } from "lucide-react";

// Types matching backend API responses
interface MenuItem {
  menu_item_id: number;  // Backend returns menu_item_id, not id
  name: string;
  description: string;
  price: number;
  image_url: string | null;
  is_vegetarian: boolean;
  is_vegan: boolean;
  is_gluten_free: boolean;
  is_spicy: boolean;
  allergens: string[];
}

interface MenuSection {
  name: string;
  item_count: number;
  items: MenuItem[];
}

interface CartItem {
  menu_item_id: number;
  item_name: string;
  quantity: number;
  price: number;
}

interface SessionInfo {
  success: boolean;
  session_id: string;
  restaurant_name: string;
  rest_id: number;
  status: string;
  cart_item_count: number;
  cart_subtotal: number;
  expires_in_seconds: number;
}

// API base URL - use relative path for same-origin requests
const API_BASE = "";

export default function MenuBrowser() {
  const { sessionId } = useParams<{ sessionId: string }>();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [sections, setSections] = useState<MenuSection[]>([]);
  const [activeSection, setActiveSection] = useState<string>("");
  const [cart, setCart] = useState<Map<number, CartItem>>(new Map());
  const [cartTotal, setCartTotal] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showCartDrawer, setShowCartDrawer] = useState(false);

  // Debounce timer for cart updates
  const cartSyncTimer = useRef<NodeJS.Timeout | null>(null);
  const pendingUpdates = useRef<Map<number, CartItem>>(new Map());

  // Calculate cart totals
  useEffect(() => {
    let total = 0;
    let count = 0;
    cart.forEach((item) => {
      total += item.price * item.quantity;
      count += item.quantity;
    });
    setCartTotal(total);
    setCartCount(count);
  }, [cart]);

  // Load session and menu on mount
  useEffect(() => {
    if (!sessionId) {
      setError("Invalid menu link. Please ask for a new link.");
      setLoading(false);
      return;
    }

    loadSession();
  }, [sessionId]);

  const loadSession = async () => {
    try {
      // First validate session
      const sessionRes = await fetch(`${API_BASE}/api/dmb/session/${sessionId}`);
      if (!sessionRes.ok) {
        const err = await sessionRes.json();
        throw new Error(err.detail || "Session not found or expired");
      }
      const session: SessionInfo = await sessionRes.json();
      setSessionInfo(session);

      // Always load cart data (even if session is done, so we can show order summary)
      const cartRes = await fetch(`${API_BASE}/api/dmb/cart/${sessionId}`);
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        const cartMap = new Map<number, CartItem>();
        (cartData.items || []).forEach((item: CartItem) => {
          cartMap.set(item.menu_item_id, item);
        });
        setCart(cartMap);
      }

      if (session.status === "done") {
        setDone(true);
        setLoading(false);
        return;
      }

      // Load menu
      const menuRes = await fetch(`${API_BASE}/api/dmb/menu/${sessionId}`);
      if (!menuRes.ok) {
        throw new Error("Failed to load menu");
      }
      const menuData = await menuRes.json();
      setSections(menuData.sections || []);

      // Set first section as active
      if (menuData.sections?.length > 0) {
        setActiveSection(menuData.sections[0].name);
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
      setLoading(false);
    }
  };

  // Sync entire cart with backend (batched, returns promise)
  const syncCartToBackend = useCallback(async (cartToSync: Map<number, CartItem>): Promise<boolean> => {
    if (cartToSync.size === 0) {
      // Send empty cart to clear backend
      try {
        await fetch(`${API_BASE}/api/dmb/cart/${sessionId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [] })
        });
        return true;
      } catch (err) {
        console.error("Failed to sync empty cart:", err);
        return false;
      }
    }

    // Convert cart map to array for batch update
    const items = Array.from(cartToSync.values()).filter(item => item.quantity > 0);

    try {
      const res = await fetch(`${API_BASE}/api/dmb/cart/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items })
      });
      return res.ok;
    } catch (err) {
      console.error("Failed to sync cart:", err);
      return false;
    }
  }, [sessionId]);

  // Add or update item in cart (with debounced backend sync)
  const updateItemQuantity = useCallback((item: MenuItem, delta: number) => {
    setCart(prevCart => {
      const current = prevCart.get(item.menu_item_id);
      const newQty = Math.max(0, Math.min(10, (current?.quantity || 0) + delta));

      // Create new cart state
      const newCart = new Map(prevCart);
      if (newQty === 0) {
        newCart.delete(item.menu_item_id);
      } else {
        newCart.set(item.menu_item_id, {
          menu_item_id: item.menu_item_id,
          item_name: item.name,
          quantity: newQty,
          price: item.price
        });
      }

      // Store reference for debounced sync
      pendingUpdates.current = newCart;

      // Debounce backend sync (500ms)
      if (cartSyncTimer.current) {
        clearTimeout(cartSyncTimer.current);
      }
      cartSyncTimer.current = setTimeout(() => {
        syncCartToBackend(pendingUpdates.current);
      }, 500);

      return newCart;
    });
  }, [syncCartToBackend]);

  // Mark session as done
  const handleDone = async () => {
    if (submitting) return;
    setSubmitting(true);

    // Flush any pending cart updates first (await completion)
    if (cartSyncTimer.current) {
      clearTimeout(cartSyncTimer.current);
    }
    await syncCartToBackend(cart);

    try {
      const res = await fetch(`${API_BASE}/api/dmb/done/${sessionId}`, {
        method: "POST"
      });

      if (res.ok) {
        setDone(true);
      } else {
        alert("Failed to complete. Please try again.");
      }
    } catch (err) {
      alert("Connection error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-yellow-500 animate-spin mx-auto" />
          <p className="mt-4 text-white text-lg">Loading menu...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto" />
          <h1 className="mt-4 text-2xl font-bold text-white">Menu Unavailable</h1>
          <p className="mt-2 text-gray-400">{error}</p>
          <p className="mt-4 text-sm text-gray-500">
            Return to your call and ask for a new menu link.
          </p>
        </div>
      </div>
    );
  }

  // Done state
  if (done) {
    // Get cart items as array for display
    const cartItems = Array.from(cart.values());

    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md w-full">
          <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
            <Phone className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="mt-6 text-2xl font-bold text-white">All Set!</h1>

          {cartCount > 0 ? (
            <>
              <p className="mt-2 text-gray-400">
                Return to your call. The AI will confirm your selections.
              </p>

              {/* Order Summary */}
              <div className="mt-6 bg-gray-800 rounded-xl p-4 text-left">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">
                  Your Order
                </h2>
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div key={item.menu_item_id} className="flex justify-between items-start">
                      <div className="flex-1">
                        <span className="text-white">{item.item_name}</span>
                        <span className="text-gray-500 ml-2">× {item.quantity}</span>
                      </div>
                      <span className="text-yellow-500 font-medium ml-4">
                        ${(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="border-t border-gray-700 mt-4 pt-3 flex justify-between">
                  <span className="text-gray-300 font-semibold">Subtotal</span>
                  <span className="text-yellow-500 font-bold text-lg">
                    ${cartTotal.toFixed(2)}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <p className="mt-2 text-gray-400">
              Return to your call to continue ordering with the AI.
            </p>
          )}

          {/* Back to Menu button */}
          <button
            onClick={() => setDone(false)}
            className="mt-6 px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-full transition-colors text-sm"
          >
            ← Back to Menu
          </button>
        </div>
      </div>
    );
  }

  // Get active section items
  const activeItems = sections.find(s => s.name === activeSection)?.items || [];

  return (
    <div className="min-h-screen bg-gray-900 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-20 bg-gray-900/95 backdrop-blur border-b border-gray-800">
        <div className="px-4 py-3">
          <div className="flex items-center gap-2">
            <ChefHat className="w-6 h-6 text-yellow-500" />
            <h1 className="text-lg font-bold text-white truncate">
              {sessionInfo?.restaurant_name || "Menu"}
            </h1>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Tap items to add to your order
          </p>
        </div>

        {/* Section tabs */}
        {sections.length > 1 && (
          <div className="overflow-x-auto hide-scrollbar">
            <div className="flex gap-2 px-4 pb-3">
              {sections.map((section) => (
                <button
                  key={section.name}
                  onClick={() => setActiveSection(section.name)}
                  className={`
                    whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors
                    ${activeSection === section.name
                      ? "bg-yellow-500 text-black"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                    }
                  `}
                >
                  {section.name}
                </button>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Menu items */}
      <main className="px-4 py-4">
        <div className="space-y-4">
          {activeItems.map((item) => {
            const cartItem = cart.get(item.menu_item_id);
            const quantity = cartItem?.quantity || 0;

            return (
              <div
                key={item.menu_item_id}
                className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700"
              >
                <div className="flex">
                  {/* Image */}
                  {item.image_url && (
                    <div className="w-28 h-28 flex-shrink-0">
                      <img
                        src={item.image_url}
                        alt={item.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="flex-1 p-3 flex flex-col">
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-semibold text-white leading-tight">
                          {item.name}
                        </h3>
                        <span className="text-yellow-500 font-bold whitespace-nowrap">
                          ${item.price.toFixed(2)}
                        </span>
                      </div>

                      {item.description && (
                        <p className="text-gray-400 text-sm mt-1 line-clamp-2">
                          {item.description}
                        </p>
                      )}

                      {/* Dietary flags */}
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {item.is_vegetarian && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                            <Leaf className="w-3 h-3" /> Veg
                          </span>
                        )}
                        {item.is_vegan && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Vegan
                          </span>
                        )}
                        {item.is_gluten_free && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                            GF
                          </span>
                        )}
                        {item.is_spicy && (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            🌶️ Spicy
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Quantity controls - Always show Add button, qty controls appear when added */}
                    <div className="flex items-center justify-end mt-2 gap-2">
                      {quantity > 0 && (
                        <>
                          {/* Qty controls on the left */}
                          <div className="flex items-center gap-1 bg-gray-700 rounded-full px-1 py-0.5">
                            <button
                              onClick={() => updateItemQuantity(item, -1)}
                              className="w-7 h-7 flex items-center justify-center text-yellow-500 hover:bg-gray-600 rounded-full transition-colors"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span className="text-white font-semibold w-5 text-center text-sm">
                              {quantity}
                            </span>
                            <button
                              onClick={() => updateItemQuantity(item, 1)}
                              className="w-7 h-7 flex items-center justify-center text-yellow-500 hover:bg-gray-600 rounded-full transition-colors"
                              disabled={quantity >= 10}
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          {/* Added indicator */}
                          <span className="text-green-400 text-xs font-medium">
                            ✓ Added
                          </span>
                        </>
                      )}
                      {quantity === 0 && (
                        <button
                          onClick={() => updateItemQuantity(item, 1)}
                          className="flex items-center gap-1.5 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-full transition-colors"
                        >
                          <Plus className="w-4 h-4" />
                          Add
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {activeItems.length === 0 && (
          <div className="text-center py-12">
            <ChefHat className="w-12 h-12 text-gray-600 mx-auto" />
            <p className="mt-4 text-gray-500">No items in this section</p>
          </div>
        )}
      </main>

      {/* Sticky cart footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur border-t border-gray-800 px-4 py-3 z-30">
        <div className="flex items-center justify-between gap-4">
          {/* Clickable cart section */}
          <button
            onClick={() => setShowCartDrawer(true)}
            className="flex items-center gap-3 hover:bg-gray-800 rounded-lg px-2 py-1 -ml-2 transition-colors"
          >
            <div className="relative">
              <ShoppingCart className="w-6 h-6 text-yellow-500" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 bg-yellow-500 text-black text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </div>
            <div className="text-left">
              <p className="text-white font-semibold">
                ${cartTotal.toFixed(2)}
              </p>
              <p className="text-gray-500 text-xs">
                {cartCount} {cartCount === 1 ? "item" : "items"} • Tap to view
              </p>
            </div>
          </button>

          <button
            onClick={handleDone}
            disabled={submitting}
            className={`
              flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all
              ${submitting
                ? "bg-gray-700 text-gray-400 cursor-not-allowed"
                : "bg-yellow-500 hover:bg-yellow-400 text-black"
              }
            `}
          >
            {submitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Phone className="w-5 h-5" />
            )}
            Done
          </button>
        </div>
      </footer>

      {/* Cart Drawer */}
      {showCartDrawer && (
        <div className="fixed inset-0 z-50">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowCartDrawer(false)}
          />

          {/* Drawer */}
          <div className="absolute bottom-0 left-0 right-0 bg-gray-900 rounded-t-2xl max-h-[70vh] overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-yellow-500" />
                Your Order
              </h2>
              <button
                onClick={() => setShowCartDrawer(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-800 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            {/* Cart items */}
            <div className="overflow-y-auto max-h-[calc(70vh-140px)] p-4">
              {cartCount === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="w-12 h-12 text-gray-600 mx-auto" />
                  <p className="mt-3 text-gray-500">Your cart is empty</p>
                  <p className="text-sm text-gray-600 mt-1">Tap items to add them</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Array.from(cart.values()).map((item) => (
                    <div
                      key={item.menu_item_id}
                      className="flex items-center justify-between bg-gray-800 rounded-xl p-3"
                    >
                      <div className="flex-1">
                        <p className="text-white font-medium">{item.item_name}</p>
                        <p className="text-gray-500 text-sm">
                          ${item.price.toFixed(2)} each
                        </p>
                      </div>

                      {/* Quantity controls */}
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1 bg-gray-700 rounded-full px-1 py-0.5">
                          <button
                            onClick={() => {
                              const menuItem = sections
                                .flatMap(s => s.items)
                                .find(i => i.menu_item_id === item.menu_item_id);
                              if (menuItem) updateItemQuantity(menuItem, -1);
                            }}
                            className="w-7 h-7 flex items-center justify-center text-yellow-500 hover:bg-gray-600 rounded-full transition-colors"
                          >
                            <Minus className="w-3.5 h-3.5" />
                          </button>
                          <span className="text-white font-semibold w-5 text-center text-sm">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => {
                              const menuItem = sections
                                .flatMap(s => s.items)
                                .find(i => i.menu_item_id === item.menu_item_id);
                              if (menuItem) updateItemQuantity(menuItem, 1);
                            }}
                            className="w-7 h-7 flex items-center justify-center text-yellow-500 hover:bg-gray-600 rounded-full transition-colors"
                            disabled={item.quantity >= 10}
                          >
                            <Plus className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <span className="text-yellow-500 font-semibold min-w-[60px] text-right">
                          ${(item.price * item.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer with total */}
            {cartCount > 0 && (
              <div className="border-t border-gray-800 p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-300 font-semibold">Subtotal</span>
                  <span className="text-yellow-500 font-bold text-xl">
                    ${cartTotal.toFixed(2)}
                  </span>
                </div>
                <button
                  onClick={() => {
                    setShowCartDrawer(false);
                    handleDone();
                  }}
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-full transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Done - Return to Call
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hide scrollbar styles + animations */}
      <style>{`
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
